import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
} from "@mui/material";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { login, clearError } from "./authSlice";
import logo from "../../assets/logo.png";

const validationSchema = Yup.object({
  username: Yup.string()
    .min(3, "Min 3 characters")
    .matches(/^[a-zA-Z0-9._-]+$/, "Letters, numbers, dot, underscore or dash only")
    .required("Required"),
  password: Yup.string().min(4, "Min 4 characters").required("Required"),
});

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error } = useSelector((s) => s.auth);
  const [showPassword, setShowPassword] = useState(false);
  const isLoading = status === "loading";

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(login(values));
      if (login.fulfilled.match(result)) {
        toast.success("Welcome back");
        navigate("/");
      }
    },
  });

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 460,
        p: { xs: 3, sm: 4.5 },
        borderRadius: "20px",
        border: "1px solid",
        borderColor: "rgba(13,148,136,0.18)",
        boxShadow: "0 24px 70px -36px rgba(15,30,28,0.36)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(250,253,252,0.94) 100%)",
        backdropFilter: "blur(10px)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "0 0 auto 0",
          height: 5,
          background: "linear-gradient(90deg, #0d9488 0%, #22d3ee 55%, #10b981 100%)",
        },
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          mx: "auto",
          mb: 4,
          borderRadius: "18px",
          background:
            "linear-gradient(135deg, rgba(204,251,241,0.95) 0%, rgba(207,250,254,0.95) 100%)",
          border: "1px solid",
          borderColor: "rgba(13,148,136,0.28)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 0.9,
          boxShadow: "0 14px 30px -18px rgba(13,148,136,0.75)",
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Jothi"
          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2.25 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2.5}>
          <Box>
            <TextField
              fullWidth
              name="username"
              placeholder="Username"
              autoComplete="username"
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.username && Boolean(formik.errors.username)}
              helperText={formik.touched.username && formik.errors.username}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircleOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
                sx: { height: 50, fontSize: "0.9375rem", borderRadius: 2 },
              }}
            />
          </Box>

          <Box>
            <TextField
              fullWidth
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              autoComplete="current-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { height: 50, fontSize: "0.9375rem", borderRadius: 2 },
              }}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              minHeight: 52,
              fontSize: "0.9375rem",
              fontWeight: 800,
              mt: 1,
              borderRadius: 2,
              background: "linear-gradient(135deg, #0d9488 0%, #0e7490 100%)",
              boxShadow: "0 16px 26px -18px rgba(13,148,136,0.9)",
              "&:hover": {
                background: "linear-gradient(135deg, #0f766e 0%, #155e75 100%)",
                boxShadow: "0 18px 30px -18px rgba(13,148,136,0.95)",
              },
            }}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
}
